import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { TABLE, type TableSpec } from '../config';

export type CameraMode = 'orbit' | 'aim' | 'top' | 'action' | 'break';

/** Default floor on the framed (non-orbit) camera distance multiplier — how far
 *  in the view may push. Lowerable at runtime via setZoomInMax (tuning panel). */
const ZOOM_MIN_DEFAULT = 0.45;
/** Default cap on how far the framed cameras pull back. Raisable at runtime via
 *  setZoomOutMax (tuning panel) — see the zoomMax field. */
const ZOOM_MAX_DEFAULT = 4;
/** Bounds the tuning panel offers for that cap. */
export const ZOOM_OUT_MIN = 2;
export const ZOOM_OUT_MAX = 12;
/** Bounds the tuning panel offers for the zoom-in floor (smaller = closer). */
export const ZOOM_IN_MIN = 0.12;
export const ZOOM_IN_MAX = 1.5;
/**
 * Multiplier on every wheel/key/pinch zoom step. A narrow field of view already
 * magnifies, so the same distance step reads as a much bigger jump than it does
 * at a wide FOV — this is the dial that re-tunes step size to match the lens.
 */
export const ZOOM_RATE_DEFAULT = 1;
export const ZOOM_RATE_MIN = 0.2;
export const ZOOM_RATE_MAX = 4;
/** How far the aim camera zooms in while fine-aiming (Shift): smaller = closer. */
const FINE_AIM_ZOOM = 0.5;
/** Zoom at which the swing-break plan view frames the whole table; zooming out
 *  past this does nothing (the fit IS the widest useful view), zooming in still
 *  tightens it. */
const BREAK_ZOOM_MAX = 1.25;
/**
 * Slack around the table in the break view: 1 / this is how much of the frame
 * the table spans, so 1.25 leaves it at ~80% — room for the off-table cue and
 * the swing. (Was 1.09, i.e. a tight fit; pulled back a further 15% because the
 * swing reads better with air around the table.) The view is FITTED rather than
 * placed at a fixed height because the fit depends on the lens: at a narrow FOV
 * the camera must stand much further back to hold the same framing.
 */
const BREAK_FIT_MARGIN = 1.25;
/**
 * The break camera's horizontal offset as a FRACTION of its height (it stands
 * off to one side so the swinging cue is in shot). Proportional rather than a
 * fixed 0.5 m so the view keeps its slant at every height — which also makes the
 * framing self-similar, and therefore solvable. 0.21 reproduces the old 0.5 m
 * offset at the height the view used to sit at.
 */
const BREAK_TILT = 0.21;
/**
 * Slack around the table in the top-down view, same role as BREAK_FIT_MARGIN.
 * Bigger than that margin needs to be on its own: the top view has no camera
 * tilt to duck the corner HUD panels (scoreboard top-left, shot controls
 * bottom-right) under, so a tight fit leaves them sitting on top of the rails.
 * 1.6 pulls the table in to ~60% of the frame's shorter dimension, enough
 * headroom on typical phone/desktop viewports for those panels to clear it.
 */
const TOP_FIT_MARGIN = 1.6;
/** Zoom at which the top-down view frames the whole table; zooming out past
 *  this does nothing, zooming in still tightens it (mirrors BREAK_ZOOM_MAX). */
const TOP_ZOOM_MAX = 1.5;
/**
 * Height of the top view's orthographic camera above the cloth. Framing
 * doesn't depend on this at all (that's the point of orthographic — see
 * updateTop), only clearance does: comfortably above anything that sits on
 * or over the table (balls, a raised cue), comfortably below the overhead
 * lamps (~1.7m) and the room's ceiling (CEILING_Z_DEFAULT) — this camera
 * looks straight down, so unlike every other mode it can't duck under
 * fixtures at an angle; go above them and they're what renders.
 */
const TOP_CAMERA_HEIGHT = 1.3;
/** World up (scene is z-up), for building the break camera's basis. */
const UP_AXIS = new THREE.Vector3(0, 0, 1);
const CORNER_SIGNS = [-1, 1] as const;
/** Camera height ceiling (world z, metres above the cloth) — keeps zoom-out and
 *  free-orbit from rising up through the hall roof. The overhead lamps sit ~1.7 m
 *  up, so this leaves headroom while staying under the ceiling. */
const CEILING_Z_DEFAULT = 2.4;
/**
 * Horizontal pull-back limit (world xy, metres from the table centre) for every
 * framed (non-orbit) camera mode. Without this, stacking a big zoom-out with the
 * chalking step-back (setChalking) and a narrow FOV (which itself stands the
 * camera further back to hold framing) can multiply out to a camera position
 * outside the modelled hall's walls. Free-orbit has its own distance clamp
 * (ORBIT_MAX_DIST_DEFAULT) and isn't affected by this one.
 */
const ROOM_RADIUS_DEFAULT = 4.5;
/** Default orbit-mode pull-back limit; scales with the zoom-out cap. */
const ORBIT_MAX_DIST_DEFAULT = 14;
/** Default orbit-mode push-in limit; scales with the zoom-in floor. */
const ORBIT_MIN_DIST_DEFAULT = 0.4;
/**
 * Vertical field of view, degrees. This is the "how curved does the table look"
 * dial: a wide FOV exaggerates perspective, so the long rails bow outward and
 * the near end of the table looms — the wide-angle-lens look. Narrower is more
 * telephoto: flatter, straighter rails, closer to how a table reads in person.
 * 30° is the calm, near-telephoto default (this shipped at 50°, then 40°); the
 * tuning panel can take it anywhere in [FOV_MIN, FOV_MAX].
 */
export const FOV_DEFAULT = 30;
export const FOV_MIN = 20;
export const FOV_MAX = 75;
/**
 * The FOV the framed cameras' distances were tuned at. They scale by
 * tan(FRAME_REF_FOV/2) / tan(fov/2), so how much table you see does NOT change
 * when you change the lens — a narrower FOV steps the camera back rather than
 * magnifying everything. Without this, dropping to 30° silently zoomed every
 * shot in by ~36%, which is not what a field-of-view control should do: it is a
 * perspective dial, not a zoom.
 */
const FRAME_REF_FOV = 40;

/**
 * One perspective camera with several modes. Orbit is user-driven via
 * OrbitControls; the others compute a target pose each frame and the camera
 * eases toward it (smooth transitions between modes). Scene is z-up.
 */
export class CameraRig {
  readonly camera: THREE.PerspectiveCamera;
  /** The top-down view's own camera — orthographic, so it has none of the
   *  wide-angle stretching a perspective lens shows near the frame edges
   *  (see FOV_DEFAULT's comment). Only ever active while mode === 'top';
   *  render with `renderCamera`, not `camera`, so the right one is used. */
  readonly orthoCamera: THREE.OrthographicCamera;
  readonly controls: OrbitControls;
  private mode: CameraMode = 'orbit';

  private cueBall = new THREE.Vector3(0, -TABLE.playLength / 4, 0.03);
  private aimAzimuth = Math.PI / 2;
  private elevation = 0;
  private tableLength = TABLE.playLength;
  private tableWidth = TABLE.playWidth;
  /** User zoom for the framed cameras (larger = further out). Initialised so the
   *  human-facing readout (displayZoom, mirrored) defaults to 1.6×. */
  private zoom = ZOOM_MIN_DEFAULT + ZOOM_MAX_DEFAULT - 1.6;
  /** Live cap on pulling back, and the matching camera-height ceiling. */
  private zoomMax = ZOOM_MAX_DEFAULT;
  /** Live floor on pushing in (smaller = the view may get closer). */
  private zoomMin = ZOOM_MIN_DEFAULT;
  /** Scales each zoom step so the step size can be matched to the lens. */
  private zoomRate = ZOOM_RATE_DEFAULT;
  private ceilingZ = CEILING_Z_DEFAULT;
  private roomRadius = ROOM_RADIUS_DEFAULT;
  /** The player's chosen field of view. `camera.fov` can differ: the break view
   *  borrows a wider lens when the ceiling stops it pulling back far enough. */
  private userFov = FOV_DEFAULT;
  /**
   * Right-drag free orbit of the aim camera around the cue ball. When active it
   * decouples from the aim direction (absolute angles), so the view holds the
   * angle you set instead of snapping back behind the cue as you aim.
   */
  private viewOverride = false;
  private viewAzim = 0; // absolute camera azimuth around the cue ball
  private viewPitch = 0; // absolute camera elevation angle (radians)
  /** Shift fine-aim: zooms the aim camera in toward the target line. */
  private fineAim = false;
  /** Chalking: step the aim camera back from the table to chalk the tip. */
  private chalking = false;

  private readonly desiredPos = new THREE.Vector3();
  private readonly lookTarget = new THREE.Vector3();
  // scratch vectors for the break-view fit (called in a bisection loop, so it
  // must not allocate)
  private readonly scratchA = new THREE.Vector3();
  private readonly scratchB = new THREE.Vector3();
  private readonly scratchC = new THREE.Vector3();
  private readonly scratchD = new THREE.Vector3();
  private readonly scratchE = new THREE.Vector3();
  /** Screen-up for the top-down view: world +Y (portrait) or +X (landscape),
   *  so the table's long axis always runs the long way across the window. */
  private readonly topUp = new THREE.Vector3(0, 1, 0);
  /** Cue ball position captured the moment top view is entered — decides which
   *  way is "up" on screen (see updateTop) so the player's own end of the
   *  table lands toward the bottom, continuing what they were just looking
   *  at, instead of an orientation that can flip depending only on the
   *  window's aspect. */
  private readonly topAnchor = new THREE.Vector2(0, -1);
  /** Current (eased) half-height of the top view's orthographic frustum. */
  private topHalfHeight = TABLE.playLength;

  constructor(domElement: HTMLElement) {
    this.camera = new THREE.PerspectiveCamera(FOV_DEFAULT, 1, 0.01, 100);
    this.camera.up.set(0, 0, 1);
    this.camera.position.set(0, -TABLE.playLength * 0.75, TABLE.playLength * 0.7);

    this.orthoCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.01, 100);
    this.orthoCamera.up.set(0, 0, 1);

    this.controls = new OrbitControls(this.camera, domElement);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.08;
    this.controls.target.set(0, 0, 0);
    this.controls.maxPolarAngle = Math.PI * 0.49; // don't go under the table
    this.controls.minDistance = ORBIT_MIN_DIST_DEFAULT;
    this.controls.maxDistance = ORBIT_MAX_DIST_DEFAULT; // allow zooming well out for a wide view
    // Right button orbits (like leaving aim mode and looking around), middle
    // button pans; the left button is reserved for entering aim mode.
    this.controls.enablePan = true;
    this.controls.mouseButtons = { LEFT: null, MIDDLE: THREE.MOUSE.PAN, RIGHT: THREE.MOUSE.ROTATE };
    this.controls.update();
  }

  /**
   * Switch to free-orbit, seeding OrbitControls from the camera's live pose so
   * the view holds exactly the angle it had (no jump). The pivot is placed where
   * the current view meets the table (z=0), or a fixed distance ahead otherwise.
   */
  enterOrbitFromCurrent(): void {
    const forward = new THREE.Vector3();
    this.camera.getWorldDirection(forward);
    let dist = 1.5;
    if (forward.z < -1e-3) {
      // distance along the view to the table surface, clamped to sane orbit range
      dist = Math.min(this.controls.maxDistance, Math.max(this.controls.minDistance, -this.camera.position.z / forward.z));
    }
    this.controls.target.copy(this.camera.position).addScaledVector(forward, dist);
    this.mode = 'orbit';
    this.viewOverride = false;
    this.controls.enabled = true;
    this.controls.update();
  }

  /** Update the framing reference when the variant's table size changes. */
  setTable(table: TableSpec): void {
    this.tableLength = table.playLength;
    this.tableWidth = table.playWidth;
  }

  /**
   * Vertical field of view in degrees — how much perspective distortion the view
   * has. Lower flattens the table (rails read straighter); higher exaggerates it.
   * Distinct from zoom, which moves the camera instead of changing the lens.
   */
  get fov(): number {
    return this.userFov;
  }

  setFov(deg: number): void {
    this.userFov = Math.max(FOV_MIN, Math.min(FOV_MAX, deg));
    this.applyLens(this.userFov);
  }

  /**
   * Put a lens on the camera. Normally the player's own FOV; the break view may
   * pass a wider one when the roof stops it pulling back far enough to frame the
   * table. Cheap to call every frame — it only touches the projection matrix
   * when the value actually changes.
   */
  private applyLens(deg: number): void {
    const next = Math.max(FOV_MIN, Math.min(FOV_MAX, deg));
    if (Math.abs(next - this.camera.fov) < 0.01) return;
    this.camera.fov = next;
    this.camera.updateProjectionMatrix();
  }

  /**
   * How far outside the frame the table's worst corner sits, for a break camera
   * at height `z` with vertical FOV `deg`. 1 = exactly touching the edge, so
   * anything at or under 1 has the whole table on screen.
   *
   * Done by projecting the corners rather than with a closed form, because the
   * break camera is TILTED (it stands off to one side so the swinging cue is in
   * shot). The tilt is what a width/height formula gets wrong: it costs almost
   * nothing when the camera is high, and crops a rail badly when a wide FOV
   * brings the camera down low.
   */
  private breakOverflow(z: number, deg: number): number {
    const halfTan = Math.tan((deg * Math.PI) / 360);
    const aspect = this.camera.aspect > 0 ? this.camera.aspect : 1;
    const cam = this.scratchA.set(-BREAK_TILT * z, 0, z);
    // camera basis: forward toward the table centre, then right/up from world up
    const fwd = this.scratchB.copy(cam).negate().normalize();
    const right = this.scratchC.crossVectors(fwd, UP_AXIS).normalize();
    const up = this.scratchD.crossVectors(right, fwd);
    let worst = 0;
    for (const sx of CORNER_SIGNS) {
      for (const sy of CORNER_SIGNS) {
        const v = this.scratchE.set((sx * this.tableWidth) / 2 - cam.x, (sy * this.tableLength) / 2 - cam.y, -cam.z);
        const depth = v.dot(fwd);
        if (depth <= 1e-4) return Infinity; // corner behind the camera: never fits
        const nx = Math.abs(v.dot(right)) / (depth * halfTan * aspect);
        const ny = Math.abs(v.dot(up)) / (depth * halfTan);
        worst = Math.max(worst, nx, ny);
      }
    }
    return worst * BREAK_FIT_MARGIN; // ask for a little slack around the rails
  }

  /**
   * Lowest camera height that frames the whole table at `deg`. Overflow falls
   * monotonically as the camera rises, so a bisection lands on it exactly — and
   * unlike a formula it stays correct however the view's tilt or the table's
   * proportions change.
   */
  private breakFitHeight(deg: number): number {
    let lo = 0.2;
    let hi = 40;
    for (let i = 0; i < 24; i++) {
      const mid = (lo + hi) / 2;
      if (this.breakOverflow(mid, deg) <= 1) hi = mid;
      else lo = mid;
    }
    return hi;
  }

  /** Narrowest lens that frames the whole table from height `z`, or null if even
   *  the widest one can't (very tall windows) — the caller then has to climb. */
  private breakFitFov(z: number): number | null {
    if (this.breakOverflow(z, FOV_MAX) > 1) return null;
    let lo = this.userFov;
    let hi = FOV_MAX;
    for (let i = 0; i < 24; i++) {
      const mid = (lo + hi) / 2;
      if (this.breakOverflow(z, mid) <= 1) hi = mid;
      else lo = mid;
    }
    return hi;
  }

  /**
   * How far the camera may pull back. Raising this lifts three limits at once,
   * because any one of them alone binds first and makes "zoom out" do nothing:
   * the framed-camera zoom cap, orbit mode's max distance, and the camera-height
   * ceiling (without that last one the view slides backwards instead of rising,
   * so you end up staring across the cloth rather than down at it).
   *
   * Past the default the camera will clip up through the hall roof — that's the
   * trade for a wide view, and why it isn't the default.
   */
  get zoomOutMax(): number {
    return this.zoomMax;
  }

  setZoomOutMax(v: number): void {
    const next = Math.max(ZOOM_OUT_MIN, Math.min(ZOOM_OUT_MAX, v));
    this.zoomMax = next;
    const scale = next / ZOOM_MAX_DEFAULT;
    this.ceilingZ = CEILING_Z_DEFAULT * scale;
    this.roomRadius = ROOM_RADIUS_DEFAULT * scale;
    this.controls.maxDistance = ORBIT_MAX_DIST_DEFAULT * scale;
    this.zoom = Math.min(this.zoom, next); // don't leave the current zoom out of range
  }

  /**
   * How close the framed cameras may push in, as a distance multiplier — so a
   * SMALLER value means a closer maximum zoom-in. Orbit mode's own minimum
   * distance tracks it, otherwise orbit stops short of what the framed views do.
   */
  get zoomInMax(): number {
    return this.zoomMin;
  }

  setZoomInMax(v: number): void {
    const next = Math.max(ZOOM_IN_MIN, Math.min(ZOOM_IN_MAX, v));
    this.zoomMin = next;
    this.controls.minDistance = ORBIT_MIN_DIST_DEFAULT * (next / ZOOM_MIN_DEFAULT);
    this.zoom = Math.max(this.zoom, next); // don't leave the current zoom out of range
  }

  /**
   * Step multiplier for wheel/key/pinch zooming. The right value depends on the
   * field of view: at a narrow (telephoto) FOV the same distance step swings the
   * framing much harder, so a lower rate keeps zooming controllable there.
   */
  get zoomStepRate(): number {
    return this.zoomRate;
  }

  setZoomStepRate(v: number): void {
    const next = Math.max(ZOOM_RATE_MIN, Math.min(ZOOM_RATE_MAX, v));
    this.zoomRate = next;
    this.controls.zoomSpeed = next; // orbit mode zooms via OrbitControls, not `zoom`
  }

  /** Nudge the framed-camera zoom (positive = further out), scaled by the rate. */
  adjustZoom(delta: number): number {
    this.zoom = Math.max(this.zoomMin, Math.min(this.zoomMax, this.zoom + delta * this.zoomRate));
    return this.zoom;
  }

  /** Human-facing zoom readout where a LARGER number means more zoomed IN. The
   *  internal `zoom` multiplier scales camera distance, so it runs the other way
   *  (bigger = further out); mirror it across the range for an intuitive label. */
  get displayZoom(): number {
    return this.zoomMin + this.zoomMax - this.zoom;
  }

  /** The framed-camera zoom in internal terms (larger = further out). */
  get zoomLevel(): number {
    return this.zoom;
  }

  /** Set the framed-camera zoom outright (e.g. a pulled-back spectator view). */
  setZoom(z: number): number {
    this.zoom = Math.max(this.zoomMin, Math.min(this.zoomMax, z));
    return this.zoom;
  }

  /** Right-drag: freely orbit the aim camera around the cue ball (azimuth + pitch). */
  orbitView(dx: number, dy: number): void {
    if (!this.viewOverride) {
      // seed from the current behind-the-aim pose so it starts seamlessly
      this.viewOverride = true;
      this.viewAzim = this.aimAzimuth + Math.PI;
      this.viewPitch = 0.45;
    }
    this.viewAzim -= dx * 0.003;
    this.viewPitch = Math.max(0.06, Math.min(1.45, this.viewPitch - dy * 0.0028));
  }

  /** Return to the default behind-the-cue framing (e.g. on a preset camera pick). */
  resetView(): void {
    this.viewOverride = false;
  }

  /** Shift fine-aim: zoom the aim camera in toward the target to ease precision. */
  setFineAim(on: boolean): void {
    this.fineAim = on;
  }

  /** Step back from the table while chalking the cue tip (eased automatically). */
  setChalking(on: boolean): void {
    this.chalking = on;
  }

  setMode(mode: CameraMode): void {
    if (mode === 'top' && this.mode !== 'top') {
      // Freshly entering the top view: anchor its orientation to where the cue
      // ball is RIGHT NOW, not to a fixed world direction — see updateTop.
      // Held fixed for as long as the view stays in top mode, even if the cue
      // ball itself moves (a mid-shot flip would be far more disorienting
      // than a screen side that stops quite matching after the fact).
      this.topAnchor.set(this.cueBall.x, this.cueBall.y);
    }
    this.mode = mode;
    this.controls.enabled = mode === 'orbit';
  }

  getMode(): CameraMode {
    return this.mode;
  }

  /** The camera to actually render/raycast with — the top view swaps in its
   *  own orthographic camera; every other mode uses the usual perspective one. */
  get renderCamera(): THREE.Camera {
    return this.mode === 'top' ? this.orthoCamera : this.camera;
  }

  /** Update aiming context (cue ball position + aim direction + cue elevation). */
  setAim(cueBall: THREE.Vector3, azimuth: number, elevation: number): void {
    this.cueBall.copy(cueBall);
    this.aimAzimuth = azimuth;
    this.elevation = elevation;
  }

  setActionTarget(pos: THREE.Vector3): void {
    this.cueBall.copy(pos);
  }

  resize(aspect: number): void {
    this.camera.aspect = aspect;
    this.camera.updateProjectionMatrix();
  }

  /** How much further back the framed cameras must stand to keep the framing the
   *  player's lens choice would otherwise have changed (see FRAME_REF_FOV). */
  private get lensScale(): number {
    return Math.tan((FRAME_REF_FOV * Math.PI) / 360) / Math.tan((this.userFov * Math.PI) / 360);
  }

  /** Advance the camera; returns true if it actually moved this frame (so the app
   *  can skip rendering once it has settled). */
  update(dt: number): boolean {
    if (this.mode === 'orbit') {
      this.applyLens(this.userFov); // only the break view borrows a wider lens
      const moved = this.controls.update(); // OrbitControls reports change (incl. damping)
      // keep the free-orbit camera from rising through the roof on zoom-out
      if (this.camera.position.z > this.ceilingZ) this.camera.position.z = this.ceilingZ;
      return moved;
    }
    if (this.mode === 'top') return this.updateTop(dt);

    const dir = new THREE.Vector3(Math.cos(this.aimAzimuth), Math.sin(this.aimAzimuth), 0);
    /** Break view only: the height it wants, before the roof gets a say (it
     *  already solves its own ceiling handling, so the shared clamp below
     *  skips it). */
    let breakWant = 0;
    let skipCeilingClamp = false;

    if (this.mode === 'aim') {
      const fine = this.fineAim ? FINE_AIM_ZOOM : 1; // zoom in toward the target
      const step = this.chalking ? 1.9 : 1; // step back from the table to chalk
      const lens = this.lensScale; // a narrower FOV stands further back, not closer
      const back = (0.95 + this.elevation * 0.3) * this.zoom * fine * step * lens;
      const height = (0.5 + this.elevation * 0.6) * this.zoom * fine * step * lens;
      if (this.viewOverride) {
        // free orbit around the cue ball (right-drag), independent of the aim
        const dist = Math.hypot(back, height);
        const cp = Math.cos(this.viewPitch);
        this.desiredPos.set(
          this.cueBall.x + Math.cos(this.viewAzim) * dist * cp,
          this.cueBall.y + Math.sin(this.viewAzim) * dist * cp,
          Math.max(0.05, dist * Math.sin(this.viewPitch))
        );
        this.lookTarget.copy(this.cueBall);
        this.lookTarget.z = 0.02;
      } else {
        this.desiredPos.copy(this.cueBall).addScaledVector(dir, -back);
        this.desiredPos.z = height;
        this.lookTarget.copy(this.cueBall).addScaledVector(dir, this.fineAim ? 0.85 : 0.5);
        this.lookTarget.z = 0.02;
      }
    } else if (this.mode === 'break') {
      // Plan view rotated 90°: the long axis runs left-right (table "sideways"),
      // flipped 180° (camera offset on -X) so the pinned corner reads the right
      // way up. The pose is SOLVED so the whole table is on screen at any window
      // shape and any FOV — the swing starts immediately, so a cropped rail is
      // not something the player can fix in time.
      //
      // Three ways to buy the framing, best-looking first:
      //   1. height, up to the roof — plain pull-back, no distortion,
      //   2. a wider lens for whatever the roof denied,
      //   3. height above the roof for whatever the lens couldn't take (FOV_MAX).
      // Step 3 only happens on very tall windows, and it beats cropping: looking
      // down from above the roof, the ceiling is behind the camera.
      let z = this.breakFitHeight(this.userFov);
      let lens = this.userFov;
      if (z > this.ceilingZ) {
        const wider = this.breakFitFov(this.ceilingZ);
        if (wider != null) {
          z = this.ceilingZ;
          lens = wider;
        } else {
          lens = FOV_MAX;
          z = this.breakFitHeight(FOV_MAX);
        }
      }
      this.applyLens(lens);
      // user zoom can still tighten in from the fit; it just can't pull back past it
      breakWant = z * Math.min(1, this.zoom / BREAK_ZOOM_MAX);
      this.desiredPos.set(-BREAK_TILT * breakWant, 0, breakWant);
      this.lookTarget.set(0, 0, 0);
      skipCeilingClamp = true;
    } else {
      // action: trail the cue ball, slightly above and behind the travel line
      this.desiredPos.copy(this.cueBall).addScaledVector(dir, -0.9 * this.zoom * this.lensScale);
      this.desiredPos.z = 0.7 * this.zoom * this.lensScale;
      this.lookTarget.copy(this.cueBall);
      this.lookTarget.z = 0.02;
    }

    // Never pull back past the hall's walls — chalking's step-back, a big
    // zoom-out, and a narrow FOV (which itself stands the camera further back)
    // compound multiplicatively, and uncapped can put the camera outside the
    // modelled building. Rescale toward the table centre rather than just
    // capping distance, so the view keeps looking at the table instead of
    // sliding sideways.
    const horiz = Math.hypot(this.desiredPos.x, this.desiredPos.y);
    if (horiz > this.roomRadius) {
      const s = this.roomRadius / horiz;
      this.desiredPos.x *= s;
      this.desiredPos.y *= s;
    }

    // Never frame from above the roof (caps how far zoom-out can rise). The
    // break view is exempt: it's already traded height against lens to get
    // the whole table on screen, so re-clamping here would just crop it again.
    if (!skipCeilingClamp) {
      if (this.desiredPos.z > this.ceilingZ) this.desiredPos.z = this.ceilingZ;
      this.applyLens(this.userFov); // every other mode uses the player's own lens
    }

    const k = 1 - Math.exp(-dt * 14); // frame-rate-independent easing (rigid tracking)
    const posErr = this.camera.position.distanceTo(this.desiredPos);
    this.camera.position.lerp(this.desiredPos, k);
    const m = new THREE.Matrix4().lookAt(this.camera.position, this.lookTarget, this.camera.up);
    const targetQuat = new THREE.Quaternion().setFromRotationMatrix(m);
    const rotErr = this.camera.quaternion.angleTo(targetQuat);
    this.camera.quaternion.slerp(targetQuat, k);
    // still easing toward the target pose? (lets the app render until it settles)
    return posErr > 1e-4 || rotErr > 1e-4;
  }

  /**
   * Straight overhead, orthographic — not a very-narrow-FOV perspective,
   * because no FOV is narrow enough to avoid SOME edge stretching (a
   * rectilinear projection always magnifies more toward the frame edges;
   * see FOV_DEFAULT's comment on why that reads as bowed rails at wide FOV),
   * and a plan view is exactly the case where nothing recedes into the
   * distance to hide it. Orthographic has none: every straight line reads
   * dead straight, edge to edge. Which way the table's long axis runs on
   * screen follows the window's own shape (landscape → sideways, portrait →
   * upright), and the frustum is sized so the whole table stays on screen
   * at any window shape — no roof to duck under, since there's no lens to
   * trade height against.
   */
  private updateTop(dt: number): boolean {
    const aspect = this.camera.aspect > 0 ? this.camera.aspect : 1;
    const landscape = aspect >= 1;
    const axisX = landscape ? 1 : 0;
    const axisY = landscape ? 0 : 1;
    // Flip the axis so the anchored cue-ball side lands toward the BOTTOM of
    // the screen (screen-up is where a positive dot product with topUp ends
    // up) — matching how every other camera mode frames from behind the ball.
    const anchored = this.topAnchor.x * axisX + this.topAnchor.y * axisY;
    const sign = anchored > 0 ? -1 : 1;
    this.topUp.set(axisX * sign, axisY * sign, 0);

    const longHalf = (landscape ? this.tableWidth : this.tableLength) / 2;
    const shortHalf = (landscape ? this.tableLength : this.tableWidth) / 2;
    const fitHalfHeight = Math.max(longHalf * TOP_FIT_MARGIN, (shortHalf * TOP_FIT_MARGIN) / aspect);
    // user zoom can still tighten in from the fit; it just can't pull back past it
    const wantHalfHeight = fitHalfHeight * Math.min(1, this.zoom / TOP_ZOOM_MAX);

    const k = 1 - Math.exp(-dt * 14); // frame-rate-independent easing (rigid tracking)
    const heightErr = Math.abs(wantHalfHeight - this.topHalfHeight);
    this.topHalfHeight += (wantHalfHeight - this.topHalfHeight) * k;

    const halfWidth = this.topHalfHeight * aspect;
    this.orthoCamera.left = -halfWidth;
    this.orthoCamera.right = halfWidth;
    this.orthoCamera.top = this.topHalfHeight;
    this.orthoCamera.bottom = -this.topHalfHeight;
    this.orthoCamera.updateProjectionMatrix();

    // Height just needs to clear the tallest thing on the table (the cue on
    // its backswing) — an orthographic frustum's extent doesn't depend on
    // it, so there's no reason to reach for the room's ceiling the way every
    // other mode's height/lens tradeoff does. That matters here specifically:
    // this camera looks straight DOWN, so anything above it (ceiling, the
    // overhead lamps ~1.7m up) sits directly in the shot with no oblique
    // angle to duck around, unlike the break view's tilted approach — go
    // above the roof here and you're rendering its underside, not the table.
    this.orthoCamera.position.set(0, 0, TOP_CAMERA_HEIGHT);
    const m = new THREE.Matrix4().lookAt(this.orthoCamera.position, this.scratchA.set(0, 0, 0), this.topUp);
    this.orthoCamera.quaternion.setFromRotationMatrix(m);
    this.orthoCamera.updateMatrixWorld(true);

    return heightErr > 1e-5;
  }
}
